import type {
  HTMLAttributes,
  ReactNode,
} from 'react'

import {
  cva,
  type VariantProps,
} from 'class-variance-authority'

import { cn } from '@/shared/lib/cn'

const badgeVariants = cva(
  [
    'inline-flex items-center',
    'whitespace-nowrap rounded-full',
    'border font-medium',
  ],
  {
    variants: {
      variant: {
        neutral: [
          'border-(--color-border)',
          'bg-(--color-surface-muted)',
          'text-(--color-muted-foreground)',
        ],

        primary: [
          'border-(--color-primary)/20',
          'bg-(--color-primary-muted)',
          'text-(--color-primary)',
        ],

        success: [
          'border-(--color-success)/20',
          'bg-(--color-success-muted)',
          'text-(--color-success)',
        ],

        warning: [
          'border-(--color-warning)/20',
          'bg-(--color-warning-muted)',
          'text-(--color-warning)',
        ],

        danger: [
          'border-(--color-danger)/20',
          'bg-(--color-danger-muted)',
          'text-(--color-danger)',
        ],
      },

      size: {
        small: [
          'min-h-5 gap-1',
          'px-2 py-0.5 text-xs',
        ],

        medium: [
          'min-h-6 gap-1.5',
          'px-2.5 py-1 text-xs',
        ],
      },
    },

    defaultVariants: {
      variant: 'neutral',
      size: 'medium',
    },
  },
)

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<
      typeof badgeVariants
    > {
  children: ReactNode
  showDot?: boolean
}

export function Badge({
  children,
  variant,
  size,
  showDot = false,
  className,
  ...props
}: BadgeProps) {
  return (
    <span
      {...props}
      className={cn(
        badgeVariants({
          variant,
          size,
        }),
        className,
      )}
    >
      {showDot ? (
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full bg-current"
        />
      ) : null}

      {children}
    </span>
  )
}